import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Form, Button, Alert, Tab, Nav, Badge, InputGroup, Modal, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faKey, 
  faShieldAlt, 
  faEye, 
  faEyeSlash,
  faPlus,
  faTrash,
  faCheck,
  faExclamationTriangle,
  faLock,
  faBell,
  faUsers,
  faEdit,
  faCheckCircle,
  faTimesCircle,
  faClock
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from 'helpers/api';

interface APICredential {
  id: string;
  service_type: string;
  service_name: string;
  credential_format: string;
  subdomain?: string;
  custom_endpoint?: string;
  is_active: boolean;
  is_validated: boolean;
  last_validated_at?: string;
  validation_error_message?: string;
  last_used_at?: string;
  usage_count: number;
  created_at: string;
  service_config?: {
    name: string;
    auth_type: string;
    required_fields: string[];
    documentation_url: string;
    data_migration_capabilities: string[];
  };
}

interface SecuritySettingsProps {
  userId?: string;
}

const SecuritySettings: React.FC<SecuritySettingsProps> = ({ userId }) => {
  const [activeTab, setActiveTab] = useState('api-credentials');
  const [credentials, setCredentials] = useState<APICredential[]>([]);
  const [availableServices, setAvailableServices] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCredential, setSelectedCredential] = useState<APICredential | null>(null);
  
  // Form states
  const [formData, setFormData] = useState({
    service_type: '',
    service_name: '',
    api_key: '',
    api_secret: '',
    access_token: '',
    username: '',
    password: '',
    subdomain: '',
    custom_endpoint: '',
    credential_format: 'api_key'
  });

  // Security preferences
  const [securityPrefs, setSecurityPrefs] = useState({
    two_factor_enabled: false,
    email_notifications: true,
    sms_notifications: false,
    login_alerts: true,
    api_notifications: false,
    data_retention_days: 365,
    auto_logout_minutes: 60
  });

  const [showPasswords, setShowPasswords] = useState<{[key: string]: boolean}>({});

  // Load API credentials on mount
  useEffect(() => {
    fetchCredentials();
  }, []);

  const fetchCredentials = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Check if user is authenticated
      const token = localStorage.getItem('auth_token');
      if (!token) {
        setError('Authentication required. Please log in again.');
        return;
      }
      
      console.log('🔐 [SECURITY SETTINGS] Fetching API credentials...');
      const response = await axiosInstance.get('/api-credentials');
      console.log('🔐 [SECURITY SETTINGS] API response:', response.data);
      
      if (response.data.success) {
        setCredentials(response.data.data.credentials || []);
        setAvailableServices(response.data.data.available_services || {});
        console.log('✅ [SECURITY SETTINGS] Available services:', response.data.data.available_services);
      }
    } catch (err: any) {
      console.error('❌ [SECURITY SETTINGS] Error fetching credentials:', err);
      
      // Handle authentication errors specifically
      if (err.response?.status === 401 || err.response?.status === 403) {
        setError('Session expired. Please refresh the page and log in again.');
      } else {
        setError(err.response?.data?.message || 'Failed to load API credentials');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddCredential = () => {
    setFormData({
      service_type: '',
      service_name: '',
      api_key: '',
      api_secret: '',
      access_token: '',
      username: '',
      password: '',
      subdomain: '',
      custom_endpoint: '',
      credential_format: 'api_key'
    });
    setShowAddModal(true);
  };

  const handleEditCredential = (credential: APICredential) => {
    setFormData({
      service_type: credential.service_type,
      service_name: credential.service_name,
      api_key: '', // Don't pre-fill sensitive data
      api_secret: '',
      access_token: '',
      username: '',
      password: '',
      subdomain: credential.subdomain || '',
      custom_endpoint: credential.custom_endpoint || '',
      credential_format: credential.credential_format
    });
    setSelectedCredential(credential);
    setShowAddModal(true);
  };

  const handleServiceTypeChange = (serviceType: string) => {
    const serviceConfig = availableServices[serviceType];
    if (serviceConfig) {
      setFormData(prev => ({
        ...prev,
        service_type: serviceType,
        service_name: serviceConfig.name,
        credential_format: serviceConfig.auth_type
      }));
    }
  };

  const submitCredential = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validate required fields
      if (!formData.service_type || !formData.service_name) {
        throw new Error('Service type and name are required');
      }

      const serviceConfig = availableServices[formData.service_type];
      if (serviceConfig?.required_fields) {
        for (const field of serviceConfig.required_fields) {
          if (!formData[field as keyof typeof formData]) {
            throw new Error(`${field.replace('_', ' ')} is required for ${serviceConfig.name}`);
          }
        }
      }

      const response = await axiosInstance.post('/api-credentials', formData);
      
      if (response.data.success) {
        setSuccess(`API credential ${selectedCredential ? 'updated' : 'added'} successfully`);
        setShowAddModal(false);
        setSelectedCredential(null);
        fetchCredentials();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to save credential');
    } finally {
      setLoading(false);
    }
  };

  const validateCredential = async (credentialId: string) => {
    try {
      setLoading(true);
      setError(null);

      const response = await axiosInstance.post(`/api-credentials/${credentialId}/validate`);
      
      if (response.data.success) {
        setSuccess('Credential validated successfully');
        fetchCredentials();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to validate credential');
    } finally {
      setLoading(false);
    }
  };

  const deleteCredential = async () => {
    if (!selectedCredential) return;

    try {
      setLoading(true);
      setError(null);

      const response = await axiosInstance.delete(`/api-credentials/${selectedCredential.id}`);
      
      if (response.data.success) {
        setSuccess('API credential deleted successfully');
        setShowDeleteModal(false);
        setSelectedCredential(null);
        fetchCredentials();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete credential');
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = (fieldName: string) => {
    setShowPasswords(prev => ({
      ...prev,
      [fieldName]: !prev[fieldName]
    }));
  };

  const renderCredentialForm = () => (
    <Form>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Service *</Form.Label>
            <Form.Select
              value={formData.service_type}
              onChange={(e) => handleServiceTypeChange(e.target.value)}
              disabled={!!selectedCredential}
            >
              <option value="">Select a service...</option>
              {Object.entries(availableServices)
                .filter(([key]) => !credentials.some(cred => cred.service_type === key))
                .map(([key, config]: [string, any]) => (
                  <option key={key} value={key}>
                    {config.name}
                  </option>
                ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Authentication Type</Form.Label>
            <Form.Control
              type="text"
              value={formData.credential_format}
              disabled
              style={{ backgroundColor: '#f8f9fa' }}
            />
          </Form.Group>
        </Col>
      </Row>

      {/* Dynamic fields based on service type */}
      {availableServices[formData.service_type] && (
        <>
          {availableServices[formData.service_type].required_fields.includes('api_key') && (
            <Form.Group className="mb-3">
              <Form.Label>API Key *</Form.Label>
              <InputGroup>
                <Form.Control
                  type={showPasswords.api_key ? 'text' : 'password'}
                  value={formData.api_key}
                  onChange={(e) => setFormData(prev => ({ ...prev, api_key: e.target.value }))}
                  placeholder="Enter your API key"
                />
                <Button 
                  variant="outline-secondary"
                  onClick={() => togglePasswordVisibility('api_key')}
                >
                  <FontAwesomeIcon icon={showPasswords.api_key ? faEyeSlash : faEye} />
                </Button>
              </InputGroup>
            </Form.Group>
          )}

          {availableServices[formData.service_type].required_fields.includes('subdomain') && (
            <Form.Group className="mb-3">
              <Form.Label>Subdomain *</Form.Label>
              <Form.Control
                type="text"
                value={formData.subdomain}
                onChange={(e) => setFormData(prev => ({ ...prev, subdomain: e.target.value }))}
                placeholder="e.g., yourcompany"
              />
              <Form.Text className="text-muted">
                Your organization's subdomain (without .{formData.service_type.includes('followup_boss') ? 'followupboss.com' : 'kvcore.com'})
              </Form.Text>
            </Form.Group>
          )}

          {availableServices[formData.service_type].required_fields.includes('username') && (
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Username *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                    placeholder="Enter username"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Password *</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type={showPasswords.password ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                      placeholder="Enter password"
                    />
                    <Button 
                      variant="outline-secondary"
                      onClick={() => togglePasswordVisibility('password')}
                    >
                      <FontAwesomeIcon icon={showPasswords.password ? faEyeSlash : faEye} />
                    </Button>
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>
          )}

          {/* Documentation link */}
          <Alert variant="info" className="d-flex align-items-center">
            <FontAwesomeIcon icon={faKey} className="me-2" />
            <div>
              <strong>Need help finding your API credentials?</strong><br />
              <a 
                href={availableServices[formData.service_type].documentation_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-decoration-none"
              >
                View {availableServices[formData.service_type].name} Developer Documentation
              </a>
            </div>
          </Alert>

          {/* Data migration capabilities */}
          <div className="mb-3">
            <small className="text-muted">
              <strong>Data Migration Capabilities:</strong> {
                availableServices[formData.service_type].data_migration_capabilities.join(', ')
              }
            </small>
          </div>
        </>
      )}
    </Form>
  );

  const renderCredentialsTab = () => (
    <>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h5 className="mb-1">API Credentials</h5>
          <p className="text-muted mb-0">
            Manage your CRM and MLS API keys for data integration and migration
          </p>
        </div>
        <Button variant="primary" onClick={handleAddCredential}>
          <FontAwesomeIcon icon={faPlus} className="me-2" />
          Add Credential
        </Button>
      </div>

      {credentials.length === 0 ? (
        <Alert variant="info" className="text-center py-5">
          <FontAwesomeIcon icon={faKey} size="3x" className="text-muted mb-3" />
          <h5>No API Credentials Yet</h5>
          <p>Add your first API credential to start integrating with external services</p>
          <Button variant="primary" onClick={handleAddCredential}>
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Add Your First Credential
          </Button>
        </Alert>
      ) : (
        <Table responsive hover>
          <thead>
            <tr>
              <th>Service</th>
              <th>Type</th>
              <th>Status</th>
              <th>Last Used</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {credentials.map((credential) => (
              <tr key={credential.id}>
                <td>
                  <div>
                    <strong>{credential.service_name}</strong>
                    {credential.subdomain && (
                      <><br /><small className="text-muted">{credential.subdomain}</small></>
                    )}
                  </div>
                </td>
                <td>
                  <Badge bg="secondary" className="text-capitalize">
                    {credential.credential_format.replace('_', ' ')}
                  </Badge>
                </td>
                <td>
                  <div className="d-flex align-items-center">
                    {credential.is_validated ? (
                      <Badge bg="success" className="me-2">
                        <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                        Validated
                      </Badge>
                    ) : (
                      <Badge bg="warning" className="me-2">
                        <FontAwesomeIcon icon={faTimesCircle} className="me-1" />
                        Not Validated
                      </Badge>
                    )}
                    {!credential.is_active && (
                      <Badge bg="danger">Inactive</Badge>
                    )}
                  </div>
                  {credential.validation_error_message && (
                    <small className="text-danger d-block">
                      {credential.validation_error_message}
                    </small>
                  )}
                </td>
                <td>
                  {credential.last_used_at ? (
                    <div>
                      <small>{new Date(credential.last_used_at).toLocaleDateString()}</small>
                      <br />
                      <small className="text-muted">{credential.usage_count} uses</small>
                    </div>
                  ) : (
                    <small className="text-muted">Never used</small>
                  )}
                </td>
                <td>
                  <div className="d-flex gap-2">
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() => validateCredential(credential.id)}
                      disabled={loading}
                    >
                      <FontAwesomeIcon icon={faCheck} />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => handleEditCredential(credential)}
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => {
                        setSelectedCredential(credential);
                        setShowDeleteModal(true);
                      }}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </>
  );

  const renderSecurityTab = () => (
    <div>
      <h5 className="mb-4">
        <FontAwesomeIcon icon={faShieldAlt} className="me-2" />
        Account Security
      </h5>
      
      {/* Password Management */}
      <Card className="mb-4">
        <Card.Header>
          <FontAwesomeIcon icon={faLock} className="me-2" />
          Password & Authentication
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Current Password *</Form.Label>
                <InputGroup>
                  <Form.Control 
                    type={showPasswords.current_password ? 'text' : 'password'} 
                    placeholder="Enter current password" 
                  />
                  <Button 
                    variant="outline-secondary"
                    onClick={() => togglePasswordVisibility('current_password')}
                  >
                    <FontAwesomeIcon icon={showPasswords.current_password ? faEyeSlash : faEye} />
                  </Button>
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>New Password *</Form.Label>
                <InputGroup>
                  <Form.Control 
                    type={showPasswords.new_password ? 'text' : 'password'} 
                    placeholder="Enter new password" 
                  />
                  <Button 
                    variant="outline-secondary"
                    onClick={() => togglePasswordVisibility('new_password')}
                  >
                    <FontAwesomeIcon icon={showPasswords.new_password ? faEyeSlash : faEye} />
                  </Button>
                </InputGroup>
                <Form.Text className="text-muted">
                  Password must be at least 8 characters with uppercase, lowercase, and number
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Confirm New Password *</Form.Label>
                <InputGroup>
                  <Form.Control 
                    type={showPasswords.confirm_password ? 'text' : 'password'} 
                    placeholder="Confirm new password" 
                  />
                  <Button 
                    variant="outline-secondary"
                    onClick={() => togglePasswordVisibility('confirm_password')}
                  >
                    <FontAwesomeIcon icon={showPasswords.confirm_password ? faEyeSlash : faEye} />
                  </Button>
                </InputGroup>
              </Form.Group>
            </Col>
          </Row>
          <div className="d-flex justify-content-between align-items-center">
            <Button variant="primary" disabled={loading}>
              {loading ? 'Updating...' : 'Update Password'}
            </Button>
            <small className="text-muted">
              Last updated: Never
            </small>
          </div>
        </Card.Body>
      </Card>

      {/* Session Management */}
      <Card className="mb-4">
        <Card.Header>
          <FontAwesomeIcon icon={faClock} className="me-2" />
          Session & Auto-Logout
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Auto-logout after inactivity</Form.Label>
                <Form.Select
                  value={securityPrefs.auto_logout_minutes}
                  onChange={(e) => setSecurityPrefs(prev => ({ 
                    ...prev, 
                    auto_logout_minutes: parseInt(e.target.value) 
                  }))}
                >
                  <option value={15}>15 minutes</option>
                  <option value={30}>30 minutes</option>
                  <option value={60}>1 hour</option>
                  <option value={120}>2 hours</option>
                  <option value={480}>8 hours</option>
                  <option value={0}>Never</option>
                </Form.Select>
                <Form.Text className="text-muted">
                  Automatically log out after period of inactivity for security
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Active Sessions</Form.Label>
                <div className="d-flex justify-content-between align-items-center p-2 border rounded">
                  <div>
                    <small className="text-muted d-block">Current Session</small>
                    <strong>Chrome on Windows</strong>
                  </div>
                  <Badge bg="success">Active</Badge>
                </div>
                <Form.Text className="text-muted">
                  Manage devices that have access to your account
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Security Notifications */}
      <Card className="mb-4">
        <Card.Header>
          <FontAwesomeIcon icon={faBell} className="me-2" />
          Security Notifications
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Check
                type="switch"
                id="email-notifications"
                label="Email notifications for security alerts"
                checked={securityPrefs.email_notifications}
                onChange={(e) => setSecurityPrefs(prev => ({ 
                  ...prev, 
                  email_notifications: e.target.checked 
                }))}
                className="mb-3"
              />
              <Form.Check
                type="switch"
                id="login-alerts"
                label="Login alerts from new devices"
                checked={securityPrefs.login_alerts}
                onChange={(e) => setSecurityPrefs(prev => ({ 
                  ...prev, 
                  login_alerts: e.target.checked 
                }))}
                className="mb-3"
              />
            </Col>
            <Col md={6}>
              <Form.Check
                type="switch"
                id="sms-notifications"
                label="SMS notifications for critical alerts"
                checked={securityPrefs.sms_notifications}
                onChange={(e) => setSecurityPrefs(prev => ({ 
                  ...prev, 
                  sms_notifications: e.target.checked 
                }))}
                className="mb-3"
              />
              <Form.Check
                type="switch"
                id="api-notifications"
                label="API credential usage alerts"
                checked={securityPrefs.api_notifications || false}
                onChange={(e) => setSecurityPrefs(prev => ({ 
                  ...prev, 
                  api_notifications: e.target.checked 
                }))}
                className="mb-3"
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Data Security */}
      <Card className="mb-4">
        <Card.Header>
          <FontAwesomeIcon icon={faUsers} className="me-2" />
          Data & Privacy Controls
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Data Retention Period</Form.Label>
                <Form.Select
                  value={securityPrefs.data_retention_days}
                  onChange={(e) => setSecurityPrefs(prev => ({ 
                    ...prev, 
                    data_retention_days: parseInt(e.target.value) 
                  }))}
                >
                  <option value={90}>90 days</option>
                  <option value={180}>6 months</option>
                  <option value={365}>1 year</option>
                  <option value={1095}>3 years</option>
                  <option value={-1}>Keep forever</option>
                </Form.Select>
                <Form.Text className="text-muted">
                  How long to keep inactive lead and transaction data
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>MLS Data Sync</Form.Label>
                <div className="d-flex justify-content-between align-items-center p-2 border rounded">
                  <div>
                    <small className="text-muted d-block">Last Sync</small>
                    <strong>2 hours ago</strong>
                  </div>
                  <Badge bg="success">Active</Badge>
                </div>
                <Form.Text className="text-muted">
                  Automatic synchronization with MLS systems
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Save Changes Button */}
      <div className="d-flex justify-content-end">
        <Button variant="primary" size="lg" disabled={loading}>
          <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
          {loading ? 'Saving...' : 'Save Security Settings'}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="security-settings">
      {/* Alerts */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
          {error}
          {(error.includes('Session expired') || error.includes('Authentication required')) && (
            <div className="mt-2">
              <Button 
                variant="outline-danger" 
                size="sm" 
                onClick={() => window.location.reload()}
                className="me-2"
              >
                Refresh Page
              </Button>
              <small className="text-muted">
                Or try logging out and logging back in if the issue persists.
              </small>
            </div>
          )}
        </Alert>
      )}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Main Tabs */}
      <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'api-credentials')}>
        <Nav variant="tabs" className="mb-4">
          <Nav.Item>
            <Nav.Link eventKey="api-credentials">
              <FontAwesomeIcon icon={faKey} className="me-2" />
              API Credentials
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="security">
              <FontAwesomeIcon icon={faShieldAlt} className="me-2" />
              Account Security
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="privacy">
              <FontAwesomeIcon icon={faUsers} className="me-2" />
              Privacy Settings
            </Nav.Link>
          </Nav.Item>
        </Nav>

        <Tab.Content>
          <Tab.Pane eventKey="api-credentials">
            {renderCredentialsTab()}
          </Tab.Pane>
          <Tab.Pane eventKey="security">
            {renderSecurityTab()}
          </Tab.Pane>
          <Tab.Pane eventKey="privacy">
            <div>
              <h5>Privacy Settings</h5>
              <p>Privacy controls coming soon...</p>
            </div>
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>

      {/* Add/Edit Credential Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedCredential ? 'Edit API Credential' : 'Add API Credential'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {renderCredentialForm()}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submitCredential} disabled={loading}>
            {loading ? 'Saving...' : (selectedCredential ? 'Update' : 'Add')} Credential
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <FontAwesomeIcon icon={faExclamationTriangle} size="3x" className="text-warning mb-3" />
            <h5>Are you sure?</h5>
            <p>
              This will permanently delete the API credential for{' '}
              <strong>{selectedCredential?.service_name}</strong>.
            </p>
            <p className="text-danger">
              <small>This action cannot be undone.</small>
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={deleteCredential} disabled={loading}>
            {loading ? 'Deleting...' : 'Delete Credential'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SecuritySettings; 